import { Fragment } from "react";
import { Star } from "lucide-react";
import { getTestimonial } from "../../lib/blog";

/* Renders **bold** inside a text string. */
const Inline = ({ text }) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
            <strong key={i} className="font-semibold text-brand-brown">
                {part.slice(2, -2)}
            </strong>
        ) : (
            <Fragment key={i}>{part}</Fragment>
        )
    );

const P = ({ text }) => <p className="text-base leading-relaxed text-brand-brown/85 sm:text-lg">{<Inline text={text} />}</p>;

const H2 = ({ text }) => <h2 className="pt-4 font-display text-2xl font-bold tracking-tight text-brand-brown sm:text-3xl">{text}</h2>;

const OL = ({ items }) => (
    <ol className="space-y-4">
        {items.map((item, i) => (
            <li key={i} className="flex gap-4">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-orange font-mono text-sm font-semibold text-white">
                    {i + 1}
                </span>
                <span className="text-base leading-relaxed text-brand-brown/85 sm:text-lg">
                    <Inline text={item} />
                </span>
            </li>
        ))}
    </ol>
);

const UL = ({ items }) => (
    <ul className="space-y-4">
        {items.map((item, i) => (
            <li key={i} className="flex gap-4">
                <span className="mt-2.5 h-2.5 w-2.5 shrink-0 rounded-full bg-brand-orange" aria-hidden="true" />
                <span className="text-base leading-relaxed text-brand-brown/85 sm:text-lg">
                    <Inline text={item} />
                </span>
            </li>
        ))}
    </ul>
);

/* A real table on wider screens; stacked cards on phones. */
const Table = ({ head, rows }) => (
    <div>
        <div className="hidden overflow-hidden rounded-2xl border border-brand-brown/10 sm:block">
            <table className="w-full text-left text-sm">
                <thead className="bg-brand-cream">
                    <tr>
                        {head.map((h, i) => (
                            <th
                                key={i}
                                scope="col"
                                className={`px-5 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] ${
                                    i === 2 ? "text-brand-orange-dark" : "text-brand-brown"
                                }`}
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-brand-brown/10 bg-white">
                    {rows.map((r, i) => (
                        <tr key={i} className="align-top">
                            <th scope="row" className="px-5 py-4 font-semibold text-brand-brown">
                                {r[0]}
                            </th>
                            <td className="px-5 py-4 leading-relaxed text-brand-muted">{r[1]}</td>
                            <td className="bg-brand-orange-tint/40 px-5 py-4 font-medium leading-relaxed text-brand-brown">✓ {r[2]}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <div className="space-y-3 sm:hidden">
            {rows.map((r, i) => (
                <div key={i} className="rounded-2xl border border-brand-brown/10 bg-white p-4">
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-brand-brown">{r[0]}</p>
                    <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                        <span className="font-semibold">{head[1]}:</span> {r[1]}
                    </p>
                    <p className="mt-2 rounded-lg bg-brand-orange-tint/50 p-2.5 text-sm font-medium leading-relaxed text-brand-brown">
                        ✓ <span className="font-semibold">{head[2]}:</span> {r[2]}
                    </p>
                </div>
            ))}
        </div>
    </div>
);

const Steps = ({ items }) => (
    <div className="grid gap-4 sm:grid-cols-2">
        {items.map((s) => (
            <div key={s.n} className="rounded-2xl border border-brand-brown/10 bg-white p-5">
                <p className="font-mono text-sm font-semibold text-brand-orange">{s.n}</p>
                <h3 className="mt-2 font-display text-xl font-bold text-brand-brown">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted sm:text-base">{s.text}</p>
            </div>
        ))}
    </div>
);

/* The client's exact Google review, taken from TESTIMONIALS in site.js. */
const Quote = ({ name }) => {
    const t = getTestimonial(name);
    if (!t) return null;
    return (
        <figure className="rounded-2xl border-l-4 border-brand-orange bg-brand-cream p-6" data-testid="blog-quote">
            <div className="flex gap-0.5 text-brand-orange" aria-label={`${t.rating} out of 5 stars`}>
                {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" aria-hidden="true" />
                ))}
            </div>
            <blockquote className="mt-3 font-display text-lg italic leading-relaxed text-brand-brown">“{t.text}”</blockquote>
            <figcaption className="mt-4 text-sm text-brand-muted">
                <span className="font-semibold text-brand-brown">{t.name}</span> · {t.business} · {t.source}
            </figcaption>
        </figure>
    );
};

const RENDERERS = { p: P, h2: H2, ol: OL, ul: UL, table: Table, steps: Steps, quote: Quote };

export const BlogContent = ({ blocks }) => (
    <div className="space-y-6" data-testid="blog-content">
        {blocks.map((b, i) => {
            const R = RENDERERS[b.type];
            return R ? <R key={i} {...b} /> : null;
        })}
    </div>
);
